/**
 * 1stbuddha — dialog.js
 *
 * <dialog-component> wraps a native <dialog> that is styled as a drawer
 * (component-drawer.css) or a modal (component-modal.css).
 *
 * The native showModal() already provides the top layer, an inert page,
 * focus containment and Esc. This component adds:
 *   - enter / exit motion (data-state="open" | "closing")
 *   - close on backdrop click and on any [data-dialog-close] element
 *   - focus on the element marked [data-dialog-focus] (usually the heading)
 *   - focus return to the trigger and aria-expanded sync on triggers
 *   - optional auto-close when a media query matches (close-at attribute)
 *
 * Markup
 *   <button type="button" data-dialog-open="MenuDrawer"
 *           aria-controls="MenuDrawer" aria-expanded="false" aria-haspopup="dialog">
 *   <dialog-component close-at="(min-width: 64em)">
 *     <dialog id="MenuDrawer" class="drawer drawer--left" aria-labelledby="MenuDrawer-title" scroll-lock>
 *       <h2 id="MenuDrawer-title" tabindex="-1" data-dialog-focus>Menu</h2>
 *       <button type="button" data-dialog-close aria-label="Close">…</button>
 *     </dialog>
 *   </dialog-component>
 *
 * API     element.open(trigger?) · element.close() · element.isOpen
 * Events  dialog:open · dialog:close (bubble from <dialog-component>)
 */
(() => {
  if (customElements.get('dialog-component')) return;

  // Returns milliseconds. Accepts "240ms" and ".24s": Shopify's CDN minifies
  // base.css and may rewrite ms values as seconds.
  const readDuration = (token, fallback) => {
    const raw = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
    const value = parseFloat(raw);
    if (Number.isNaN(value)) return fallback;
    return /\ds$/i.test(raw) && !/ms$/i.test(raw) ? value * 1000 : value;
  };

  class DialogComponent extends HTMLElement {
    constructor() {
      super();
      this.onCancel = this.onCancel.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onClose = this.onClose.bind(this);
      this.onMediaChange = this.onMediaChange.bind(this);
    }

    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;

      this.dialog.addEventListener('cancel', this.onCancel);
      this.dialog.addEventListener('click', this.onClick);
      this.dialog.addEventListener('close', this.onClose);

      const query = this.getAttribute('close-at');
      if (query) {
        this.mediaQuery = window.matchMedia(query);
        this.mediaQuery.addEventListener('change', this.onMediaChange);
      }
    }

    disconnectedCallback() {
      if (!this.dialog) return;
      this.dialog.removeEventListener('cancel', this.onCancel);
      this.dialog.removeEventListener('click', this.onClick);
      this.dialog.removeEventListener('close', this.onClose);
      this.mediaQuery?.removeEventListener('change', this.onMediaChange);
      clearTimeout(this.closeTimer);
    }

    get isOpen() {
      return Boolean(this.dialog?.open) && this.dialog.dataset.state !== 'closing';
    }

    open(trigger = document.activeElement) {
      const { dialog } = this;
      if (!dialog || dialog.open) return;

      this.trigger = trigger instanceof HTMLElement ? trigger : null;
      dialog.showModal();

      // Commit the start position before switching state so the transition runs.
      void dialog.offsetWidth;
      dialog.dataset.state = 'open';

      const focusTarget = dialog.querySelector('[data-dialog-focus]');
      focusTarget?.focus({ preventScroll: true });

      this.setExpanded(true);
      this.dispatchEvent(new CustomEvent('dialog:open', { bubbles: true }));
    }

    close() {
      const { dialog } = this;
      if (!dialog?.open || dialog.dataset.state === 'closing') return;

      dialog.dataset.state = 'closing';

      const finish = () => {
        clearTimeout(this.closeTimer);
        dialog.removeEventListener('transitionend', onTransitionEnd);
        if (dialog.open) dialog.close();
      };
      const onTransitionEnd = (event) => {
        if (event.target === dialog && !event.pseudoElement) finish();
      };

      dialog.addEventListener('transitionend', onTransitionEnd);
      this.closeTimer = setTimeout(finish, readDuration('--duration-base', 240) + 60);
    }

    onCancel(event) {
      // Esc: run the exit motion instead of closing instantly.
      event.preventDefault();
      this.close();
    }

    onClick(event) {
      if (event.target.closest('[data-dialog-close]')) {
        this.close();
        return;
      }

      // A click on the ::backdrop targets the <dialog> itself, outside its box.
      if (event.target !== this.dialog) return;
      const rect = this.dialog.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;
      if (!inside) this.close();
    }

    onClose() {
      delete this.dialog.dataset.state;
      this.setExpanded(false);
      if (this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
      this.trigger = null;
      this.dispatchEvent(new CustomEvent('dialog:close', { bubbles: true }));
    }

    onMediaChange(event) {
      if (event.matches) this.close();
    }

    setExpanded(expanded) {
      if (!this.dialog.id) return;
      document
        .querySelectorAll(`[data-dialog-open="${CSS.escape(this.dialog.id)}"][aria-expanded]`)
        .forEach((trigger) => trigger.setAttribute('aria-expanded', String(expanded)));
    }
  }

  customElements.define('dialog-component', DialogComponent);

  // Any element with data-dialog-open="<dialog id>" opens that dialog.
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-dialog-open]');
    if (!trigger) return;

    const dialog = document.getElementById(trigger.dataset.dialogOpen);
    const component = dialog?.closest('dialog-component');
    if (!component) return;

    event.preventDefault();
    component.open(trigger);
  });
})();
